<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';

const { contentAttributes } = useMessageContext();
const { t } = useI18n();

const referral = computed(() => contentAttributes.value?.referral || null);
// WhatsApp only guarantees a playable asset URL on Meta's CDN (fbcdn.net); for
// organic posts boosted as ads it can send the facebook.com page/video
// permalink instead, which can't be embedded (blocked cross-origin).
const isEmbeddableMediaUrl = url => {
  try {
    return new URL(url).hostname.endsWith('fbcdn.net');
  } catch (e) {
    return false;
  }
};
const isVideo = computed(
  () => referral.value?.media_type === 'video' && !!referral.value?.video_url
);
const mediaSrc = computed(() => {
  if (isVideo.value) return referral.value.video_url;
  return referral.value?.image_url || referral.value?.thumbnail_url || '';
});
const hasMedia = computed(
  () => !!mediaSrc.value && isEmbeddableMediaUrl(mediaSrc.value)
);

const hasMediaError = ref(false);
const onMediaError = () => {
  hasMediaError.value = true;
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <BaseBubble
    v-if="referral"
    class="!p-0 overflow-hidden !max-w-[320px] min-w-[240px]"
    data-bubble-name="referral"
    hide-meta
  >
    <div
      v-if="hasMedia && !hasMediaError"
      class="w-full max-h-[180px] overflow-hidden bg-n-solid-2"
    >
      <video
        v-if="isVideo"
        controls
        :poster="referral.thumbnail_url || undefined"
        class="w-full max-h-[180px] object-cover"
        :src="mediaSrc"
        @error="onMediaError"
      />
      <img
        v-else
        :src="mediaSrc"
        alt=""
        class="w-full max-h-[180px] object-cover skip-context-menu"
        @error="onMediaError"
      />
    </div>
    <div class="flex flex-col gap-1 p-3">
      <div class="flex items-center gap-1.5 text-n-slate-11">
        <Icon icon="i-lucide-megaphone" class="size-3.5" />
        <span class="text-[0.6875rem] font-medium uppercase tracking-wide">
          {{ t('AD_REFERRAL.BADGE') }}
        </span>
      </div>
      <div
        v-if="referral.headline"
        class="font-medium leading-tight text-n-slate-12"
      >
        {{ referral.headline }}
      </div>
      <p v-if="referral.body" class="text-sm leading-snug text-n-slate-11">
        {{ referral.body }}
      </p>
      <a
        v-if="referral.source_url"
        :href="referral.source_url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 mt-1 text-sm font-medium text-n-blue-11 hover:underline w-fit skip-context-menu"
      >
        {{ t('AD_REFERRAL.VIEW_AD') }}
        <Icon icon="i-lucide-external-link" class="size-3" />
      </a>
    </div>
  </BaseBubble>
</template>
